import ScrollReveal from '@/components/feature/ScrollReveal';

const services = [
  { emoji: '🌍', label: 'International Tours', title: 'Curated international travel experiences across 50+ countries with end-to-end planning.', keywords: 'international tours, overseas travel, abroad holidays' },
  { emoji: '🗺️', label: 'Domestic Tours', title: "Discover India's hidden gems with expertly crafted domestic holiday packages.", keywords: 'domestic tours, India travel, local holidays' },
  { emoji: '📋', label: 'Customized Holiday Packages', title: 'Bespoke itineraries designed around your preferences, budget and travel style.', keywords: 'customized holidays, bespoke travel, tailor made packages' },
  { emoji: '👨‍👩‍👧‍👦', label: 'Family Holidays', title: 'Memorable family vacations with kid-friendly activities and comfortable stays.', keywords: 'family holidays, family vacation, kids travel' },
  { emoji: '👥', label: 'Group Tours', title: 'Well-organized group travel experiences with like-minded explorers and expert guides.', keywords: 'group tours, group travel, guided tours' },
  { emoji: '💼', label: 'Corporate Tours', title: 'Professional corporate travel management including MICE and business retreats.', keywords: 'corporate tours, business travel, MICE' },
  { emoji: '💑', label: 'Honeymoon Packages', title: "Romantic getaways to the world's most enchanting destinations for newlyweds.", keywords: 'honeymoon packages, romantic holidays, couple travel' },
  { emoji: '🚢', label: 'Cruise Holidays', title: 'Luxurious cruise experiences across the Mediterranean, Caribbean and Southeast Asia.', keywords: 'cruise holidays, cruise travel, luxury cruises' },
  { emoji: '✈️', label: 'Flight Ticket Bookings', title: 'Competitive airfares across all major airlines with flexible booking options.', keywords: 'flight tickets, air booking, cheap flights' },
  { emoji: '🏨', label: 'Hotel Reservations', title: 'Handpicked accommodations from boutique stays to 5-star luxury resorts worldwide.', keywords: 'hotel booking, resort reservations, luxury stay' },
  { emoji: '🛂', label: 'Visa Assistance', title: 'Hassle-free visa processing with expert guidance and documentation support.', keywords: 'visa assistance, visa processing, travel documents' },
  { emoji: '🛡️', label: 'Travel Insurance', title: 'Comprehensive travel insurance plans for worry-free international journeys.', keywords: 'travel insurance, trip insurance, travel protection' },
  { emoji: '👑', label: 'Luxury Holidays', title: 'Exclusive luxury travel experiences with premium accommodations and concierge service.', keywords: 'luxury holidays, premium travel, luxury vacations' },
  { emoji: '🚗', label: 'Airport Transfers', title: 'Seamless airport pickup and drop services in premium vehicles worldwide.', keywords: 'airport transfer, pickup service, transport' },
  { emoji: '🎓', label: 'Educational Tours', title: 'Enriching educational travel programs for schools, colleges and institutions.', keywords: 'educational tours, student travel, learning trips' },
  { emoji: '🏕️', label: 'Weekend Getaways', title: 'Quick escape packages for refreshing short breaks from your daily routine.', keywords: 'weekend getaway, short break, quick holiday' },
  { emoji: '🙏', label: 'Pilgrimage Tours', title: 'Spiritual journeys to sacred destinations across India and beyond.', keywords: 'pilgrimage tours, spiritual travel, religious tours' },
  { emoji: '📝', label: 'Customized Itineraries', title: 'Day-by-day personalized travel plans crafted by experienced travel consultants.', keywords: 'customized itineraries, travel planning, trip planner' },
  { emoji: '🏢', label: 'MICE Travel', title: 'Meetings, Incentives, Conferences and Exhibitions travel solutions for enterprises.', keywords: 'MICE travel, corporate events, business conferences' },
  { emoji: '🤝', label: 'Corporate Travel Solutions', title: 'End-to-end corporate travel management with dedicated account support.', keywords: 'corporate travel, business travel management, enterprise travel' },
];

const row1 = services.slice(0, 10);
const row2 = services.slice(10, 20);

const serviceCatalogSchema = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  itemListElement: services.map((s, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    item: {
      '@type': 'Service',
      name: s.label,
      description: s.title,
      provider: {
        '@type': 'TravelAgency',
        name: 'Lucky Holidays India Private Limited',
        url: 'https://www.luckyholidays.in/',
      },
      areaServed: {
        '@type': 'Country',
        name: 'India',
      },
    },
  })),
};

function MarqueeRow({ items, direction }: { items: typeof services; direction: 'left' | 'right' }) {
  const doubled = [...items, ...items];
  return (
    <div
      className="overflow-hidden relative py-2"
      style={{
        maskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
      }}
    >
      <div className={`flex gap-3 sm:gap-4 w-max ${direction === 'left' ? 'animate-marquee-left' : 'animate-marquee-right'}`}>
        {doubled.map((s, idx) => (
          <div
            key={`${s.label}-${idx}`}
            className="flex items-center gap-2 px-4 py-2.5 bg-background-100 border border-background-200/60 rounded-full text-foreground-700 hover:text-primary-600 hover:border-primary-300 transition-all duration-300 cursor-default flex-shrink-0 hover:scale-105"
            title={s.title}
            itemScope
            itemType="https://schema.org/Service"
          >
            <span className="text-base leading-none">{s.emoji}</span>
            <span className="text-xs font-medium whitespace-nowrap" itemProp="name">{s.label}</span>
            <meta itemProp="description" content={s.title} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ServicesSection() {
  return (
    <section id="services" className="py-10 md:py-14 bg-background-50">
      <script type="application/ld+json">{JSON.stringify(serviceCatalogSchema)}</script>
      <div className="w-full px-4 md:px-8 lg:px-16 3xl:max-w-[1800px] 3xl:mx-auto tv:max-w-[3000px] tv:mx-auto">
        <ScrollReveal>
          <div className="text-center mb-6 md:mb-8">
            <span className="inline-block px-3 py-1 bg-accent-50 text-accent-700 text-[10px] md:text-xs font-semibold tracking-widest uppercase rounded-full mb-2">
              What We Offer
            </span>
            <h2 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground-900 mb-1">
              Our Premium Travel Services
            </h2>
            <p className="text-foreground-500 text-xs md:text-sm max-w-xl mx-auto">
              End-to-end travel solutions crafted for exceptional journeys across India and the world.
            </p>
          </div>
        </ScrollReveal>

        <div className="space-y-2 max-w-6xl tv:max-w-[2800px] mx-auto">
          <MarqueeRow items={row1} direction="left" />
          <MarqueeRow items={row2} direction="right" />
        </div>
      </div>
    </section>
  );
}